import React, { useContext, useEffect, useState } from 'react';
import {
  Heading,
  Container,
  useToast,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Flex,
} from '@chakra-ui/react';
import { SmallAddIcon } from '@chakra-ui/icons';
import { HttpContext } from '../context/HttpContext';
import CostCategoryList from '../components/costCategories/CostCategoryList';
import CostCategoryForm from '../components/costCategories/CostCategoryForm';
import { usePaginator } from 'chakra-paginator';
import { INITIAL_PAGE_SIZE } from '../util/consts';

export default function CostCategoriesAdmin() {
  const clearedOrderBy = {
    code: null,
    name: null,
    enabled: null,
  };
  const initialOrderBy = {
    code: null,
    name: null,
    enabled: null,
  };

  const [state, setState] = useState({
    categories: [],
    loading: true,
    total: 0,
  });
  const [order, setOrder] = useState(initialOrderBy);
  const { authAxios } = useContext(HttpContext);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { pagesQuantity, currentPage, setCurrentPage, pageSize, setPageSize, offset } =
    usePaginator({
      total: state.total,
      initialState: {
        pageSize: INITIAL_PAGE_SIZE,
        currentPage: 1,
        isDisabled: false,
      },
    });

  function getSortingQuery(order) {
    if (order.code) {
      return `&sort=code&direction=${order.code}`;
    }
    if (order.name) {
      return `&sort=name&direction=${order.name}`;
    }
    if (order.enabled) {
      const orderEnabled = order.enabled === 'desc' ? 'asc' : 'desc';
      return `&sort=enabled&direction=${orderEnabled}`;
    }
    return '';
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sort = getSortingQuery(order);
        const { data } = await authAxios.get(
          `/cost-categories?limit=${pageSize}&offset=${offset}${sort}`
        );
        const categories = (data && data.data) || [];
        const total = (data && data.meta && data.meta.total) || 0;

        setState({
          categories,
          total,
          loading: false,
        });
      } catch (onError) {
        console.log(onError);
        setState((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    };

    fetchData();
  }, [authAxios, currentPage, pageSize, offset, order]);

  function addCategory(category) {
    const oldCategories = state.categories;
    setCurrentPage(1);
    setState({
      ...state,
      categories: [category, ...oldCategories],
      total: state.total + 1,
    });
    toast({
      position: 'top',
      description: 'Cost category has been successfully created.',
      status: 'success',
      duration: 6000,
      isClosable: true,
    });
  }

  function updateCategory(category) {
    const newCategories = state.categories;
    let recordIndex = newCategories.findIndex((x) => x.id === category.id);
    newCategories[recordIndex] = { ...category };

    setState({
      ...state,
      categories: newCategories,
    });

    toast({
      position: 'top',
      description: 'Cost category has been successfully updated.',
      status: 'success',
      duration: 6000,
      isClosable: true,
    });
  }

  function removeCategory(category) {
    const newCategories = [...state.categories];
    let recordIndex = newCategories.findIndex((x) => x.id === category.id);
    if (recordIndex !== -1) {
      newCategories[recordIndex] = { ...newCategories[recordIndex], enabled: false };
    }
    setState({ ...state, categories: newCategories });
  }

  const handlePageChange = (nextPage) => {
    setCurrentPage(nextPage);
  };

  const handlePageSizeChange = (event) => {
    const pageSize = Number(event.target.value);
    setPageSize(pageSize);
    setCurrentPage(1);
  };

  function updateOrderBy(key, value) {
    setOrder({ ...clearedOrderBy, [key]: value });
    setCurrentPage(1);
  }

  return (
    <Container w="100%" maxWidth="100%" pt={4} pr={10} pl={10} pb={14}>
      <Heading as="h2" size="lg" textAlign="left" fontWeight="700" pt={4} mb={[4, 4, 5]}>
        Cost Categories
      </Heading>

      <Button
        leftIcon={<SmallAddIcon />}
        colorScheme="brand"
        borderRadius="8px"
        fontSize="14px"
        fontWeight="500"
        pt="18px"
        pb="18px"
        _hover={{ bg: '#000' }}
        _expanded={{ bg: 'black', color: 'white' }}
        _focus={{ boxShadow: 'none' }}
        onClick={onOpen}
        mb="4"
      >
        Add Cost Category
      </Button>

      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        size="full"
        blockScrollOnMount={false}
      >
        <DrawerOverlay />
        <DrawerContent style={{ overflow: 'auto' }}>
          <Container maxW="container.sm" pt={24}>
            <DrawerCloseButton color="brand.500" _focus={{ outline: '0' }} size="lg" />
            <DrawerHeader>
              <Flex alignItems="center" direction="column">
                <Heading
                  as="h2"
                  size="lg"
                  textAlign="center"
                  fontWeight="700"
                  mb={[4, 4, 8]}
                  mt={4}
                >
                  Add New Cost Category
                </Heading>
              </Flex>
            </DrawerHeader>
            <DrawerBody style={{ overflow: 'auto' }}>
              <CostCategoryForm onClose={onClose} onSuccess={addCategory} />
            </DrawerBody>
          </Container>
        </DrawerContent>
      </Drawer>

      <CostCategoryList
        categories={state.categories}
        handlePageSizeChange={handlePageSizeChange}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        pagesQuantity={pagesQuantity}
        updateCategory={updateCategory}
        removeCategory={removeCategory}
        orderBy={order}
        updateOrderBy={updateOrderBy}
        loading={state.loading}
      />
    </Container>
  );
}
